import { useTranslation } from "@next-platform/client-common-core";
import type { TabItem } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";

export enum FavoritesTab {
  CHATBOTS = "chatbots",
  GROUPS = "groups",
  LOREBOOKS = "lorebooks",
}

const FAVORITES_TAB_ROUTES: Record<FavoritesTab, string> = {
  [FavoritesTab.CHATBOTS]: ROUTES.favoriteBots,
  [FavoritesTab.GROUPS]: ROUTES.favoritesGroups,
  [FavoritesTab.LOREBOOKS]: ROUTES.favoritesLorebooks,
};

const useFavoritesTabs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasFeatureFlag: hasPublicLorebooks, isLoading: isLorebooksFlagLoading } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_DISCOVER_PUBLIC);
  const { hasFeatureFlag: hasGroupChat, isLoading: isGroupChatFlagLoading } = useFeatureFlag(FEATURE_FLAGS.GROUPCHAT);

  const tabItems = useMemo<TabItem[]>(() => {
    const items: TabItem[] = [{ name: FavoritesTab.CHATBOTS, label: t("myCreation:tab.chatbots") }];

    if (hasPublicLorebooks) {
      items.push({ name: FavoritesTab.LOREBOOKS, label: t("myCreation:tab.lorebooks") });
    }
    if (hasGroupChat) {
      items.push({ name: FavoritesTab.GROUPS, label: t("myCreation:tab.groupchats") });
    }

    return items;
  }, [hasGroupChat, hasPublicLorebooks, t]);

  const onTabChange = useCallback(
    (tabName: string) => {
      const route = FAVORITES_TAB_ROUTES[tabName as FavoritesTab] ?? ROUTES.favoriteBots;
      navigate(route);
    },
    [navigate],
  );

  return {
    tabItems,
    onTabChange,
    hasPublicLorebooks,
    hasGroupChat,
    isLorebooksFlagLoading,
    isGroupChatFlagLoading,
  };
};

export default useFavoritesTabs;
